<template>
  <div class="auto-comp-container" v-if="autoRuleComponent && autoRuleComponent.autoComponentType">
    <div v-if="!isRootLevel && firstChild" class="auto-comp-child-branch">
      <div class="auto-comp-branch-circle"></div>
      <div class="auto-comp-branch-line"></div>
    </div>
    <div :class="{'auto-comp-child': isChildComponent}">
      <div class="auto-comp" @click="componentClicked(autoRuleComponent)">
        <div :class="['auto-comp-icon', iconContainerClass]">
          <i :class="['fas', 'fa-fw', iconClass]"></i>
        </div>
        <div class="auto-comp-info">
          <div class="title">{{formatLookupName(autoRuleComponent.autoComponentType.type, AutomationComponentTypeLookup)}}: {{autoRuleComponent.autoComponentType.name}}</div>
          <div class="description" v-if="!editing"><i class="far fa-info-circle"></i>  <span v-html="formattedDescriptionHTML" /></div>
          <div class="description" v-if="editing"><i class="far fa-info-circle"></i> {{autoRuleComponent.autoComponentType.description}}</div>
          <div class="editing-details">
            <div class="my-1">
              <span v-if="hasInputObjects">
                <i class="far fa-caret-circle-up"></i> Inputs:
                <Tooltip :label="objectInfo.description" v-for="objectInfo of inputObjectList" :key="objectInfo.linkType">
                  <span :class="['label', 'auto-comp-object-type', 'input', isComponentInputTypeLinked(autoRuleComponentList, autoRuleComponent, objectInfo.linkType) ? 'linked' : 'unlinked', 'mr-2']">
                    <i :class="['fas', isComponentInputTypeLinked(autoRuleComponentList, autoRuleComponent, objectInfo.linkType) ? 'fa-check-circle' : 'fa-exclamation-circle']"></i>
                    {{formatComponentObjectLinkTypeName(objectInfo.linkType)}}
                  </span>
                </Tooltip>
              </span>
              <span v-if="hasOutputObjects">
                <i class="far fa-caret-circle-down"></i> Outputs:
                <Tooltip :label="objectInfo.description" v-for="objectInfo of outputObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type output mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </span>
              <span v-if="hasOutputChildObjects">
                <i class="far fa-caret-circle-right"></i> Branch Outputs:
                <Tooltip :label="objectInfo.description" v-for="objectInfo of outputChildObjectList" :key="objectInfo.linkType">
                  <span class="label auto-comp-object-type output mr-2">{{formatComponentObjectLinkTypeName(objectInfo.linkType)}}</span>
                </Tooltip>
              </span>
              <span v-if="hasOutputPlaceholders">
                <i class="far fa-info-circle"></i>&nbsp;
                <span class="label auto-comp-object-type output mr-2">{{outputPlaceholderList.length}} Placeholder{{displayCountPlural(outputPlaceholderList.length)}}</span>
              </span>
            </div>
          </div>
        </div>
        <div class="auto-comp-actions" v-if="editing">
          <button class="btn" @click.stop="componentDeleteClicked(autoRuleComponent)"><i class="fas fa-trash"></i></button>
        </div>
      </div>
      <div class="auto-comp-spacer">
        <div :class="['auto-comp-spacer-line', (!editing && lastChild && !isRootLevel && hasChildComponents) ? 'auto-last-child-branch' : '']" v-if="editing || !lastChild || isRootLevel || hasChildComponents" />
        <div class="auto-comp-spacer-insert" v-if="editing && !lastChild" @click.stop="componentInsertClicked(autoRuleComponent)">
          <div class="content">
            <i class="far fa-plus-circle"></i> Insert
          </div>
        </div>

        <div class="auto-comp-spacer-insert auto-comp-spacer-insert-child" v-if="editing && hasChildComponents" @click.stop="componentInsertChildClicked(autoRuleComponent)">
          <div class="content">
            <i class="far fa-plus-circle"></i> Branch Insert
          </div>
        </div>

        <AutomationRuleComponent
          v-for="(child, index) of childrenComponents"
          :key="child.id"
          :editing="editing"
          :level="level+1" 
          :parentComponentId="autoRuleComponent.id"
          :autoRuleComponentList="autoRuleComponentList"
          :autoRuleComponent="child"
          :firstChild="index == 0"
          :lastChild="index == childrenComponents.length-1"
          @componentClicked="componentClicked($event)"
          @deleteClicked="componentDeleteClicked($event)"
          @insertClicked="componentInsertClicked($event)"
          @insertChildClicked="componentInsertChildClicked($event)"
          @addComponent="componentAddClicked($event)"
        />

        <!-- Special add to empty branch -->
        <div v-if="editing && lastChild && isBranchComponent && !hasChildComponents" class="auto-comp-add-empty-branch">
          <div class="auto-comp-child-branch">
            <div class="auto-comp-branch-circle"></div>
            <div class="auto-comp-branch-line"></div>
          </div>
          <div :class="['auto-comp-container', {'auto-comp-child': true}]">
            <AutomationRuleAddComponent @click="componentAddClicked(autoRuleComponent.id)" :hideLine="true" label="Add To Branch" />
          </div>
        </div>
        
      </div>
      <div v-if="lastChild">
        <AutomationRuleAddComponent v-if="editing" @click="componentAddClicked(parentComponentId)" />
        <div class="auto-comp-rule-endpoint" v-if="!editing && isRootLevel">
          End Rule
        </div>

      </div>

    </div>
  </div>
</template>



<script>
import AutomationsMixin from '@/modules/automations/mixins/automations.js'

import AutomationRuleAddComponent from '@/modules/automations/components/automationRuleAddComponent'

import { AutomationComponentTypes, AutomationComponentTypeLookup } from '@/store/types'

export default {
  name: 'AutomationRuleComponent',
  mixins: [AutomationsMixin],
  components: {
    AutomationRuleAddComponent
  },
  props: {
    level: {
      type: Number,
      default: 0
    },
    parentComponentId: [Number, String],
    autoRuleComponentList: Array,
    autoRuleComponent: Object,
    editing: Boolean,
    firstChild: Boolean,
    lastChild: Boolean
  },
  computed: {
    iconContainerClass() {
      return `auto-comp-${this.autoRuleComponent.autoComponentType.type}`      
    },
    iconClass() {
      return this.autoRuleComponent.autoComponentType.icon
    },
    formattedDescriptionHTML() {
      var output = ('' + this.autoRuleComponent.description)
      output = output.replaceAll('[var]', '<span class="badge variable">')
      output = output.replaceAll('[/var]', '</span>')
      output = output.replaceAll('[value]', '<span class="badge value">')
      output = output.replaceAll('[/value]', '</span>')

      return output
    },
    isBranchComponent() {
      return this.autoRuleComponent.autoComponentType.type == AutomationComponentTypes.branch
    },
    isRootLevel() {
      if(!this.autoRuleComponent)
        return false
      return this.autoRuleComponent.parentAutoRuleComponentId == null
    },
    childrenComponents() {
      if(!this.autoRuleComponent)
        return []

      return this.autoRuleComponentList.filter(comp => comp.parentAutoRuleComponentId == this.autoRuleComponent.id)
    },
    hasChildComponents() {
      return this.childrenComponents.length > 0
    },
    isChildComponent() {
      return this.parentComponentId != null && this.parentComponentId != ''
    },
    hasOutputObjects() {
      return this.outputObjectList.length > 0
    },
    hasOutputChildObjects() {
      return this.outputChildObjectList.length > 0
    },
    hasInputObjects() {
      return this.inputObjectList.length > 0
    },
    hasOutputPlaceholders() {
      return this.outputPlaceholderList.length > 0
    },
    outputObjectList() {
      return this.autoRuleComponent.autoComponentType.outputTypeList || []
    },
    outputChildObjectList() {
      return this.autoRuleComponent.autoComponentType.outputChildTypeList || []
    },
    inputObjectList() {
      return this.autoRuleComponent.autoComponentType.inputTypeList || []
    },
    outputPlaceholderList() {
      return this.autoRuleComponent.autoComponentType.outputPlaceholderList || []
    }
  },
  created() {
    this.AutomationComponentTypeLookup = AutomationComponentTypeLookup
  },
  methods: {
    componentClicked(autoRuleComponent) {
      this.$emit('componentClicked', autoRuleComponent)
    },
    componentDeleteClicked(autoRuleComponent) {
      this.$emit('deleteClicked', autoRuleComponent)
    },
    componentInsertChildClicked() {
      // Get first child
      if(this.childrenComponents.length > 0)
        this.$emit('insertChildClicked', this.childrenComponents[0])
    },
    componentInsertClicked(autoRuleComponent) {
      this.$emit('insertClicked', autoRuleComponent)
    },
    componentAddClicked(parentId) {
      this.$emit('addComponent', parentId)
    }
  }
  
}
</script>